import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Filter state interface
export interface FilterState {
  tags: {
    [tagId: string]: {
      value: string;
      mode: 'inclusive' | 'required' | 'excluded'; // OR, AND, NOT
      category?: string; // Optional category for grouping
    };
  };
  searchTerm: string;
  globalLogic: 'AND' | 'OR';
}

// Filter actions
export type FilterAction =
  | { type: 'TOGGLE_TAG_MODE'; tagId: string; value: string; category?: string }
  | { type: 'SET_SEARCH_TERM'; term: string }
  | { type: 'SET_GLOBAL_LOGIC'; logic: 'AND' | 'OR' }
  | { type: 'RESET_FILTERS' }
  | { type: 'INITIALIZE_FROM_URL'; state: Partial<FilterState> };

// Filter reducer
export function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'TOGGLE_TAG_MODE': {
      const { tagId, value, category } = action;
      const currentTag = state.tags[tagId];
      
      if (!currentTag) {
        // Add new tag in inclusive mode
        return {
          ...state,
          tags: {
            ...state.tags,
            [tagId]: { value, mode: 'inclusive', category }
          }
        };
      }
      
      // Cycle through modes: inclusive -> required -> excluded -> remove
      const nextMode = {
        inclusive: 'required' as const,
        required: 'excluded' as const,
        excluded: undefined // Remove the tag
      };
      
      const newMode = nextMode[currentTag.mode];
      
      if (newMode === undefined) {
        // Remove the tag
        const { [tagId]: removedTag, ...remainingTags } = state.tags;
        return {
          ...state,
          tags: remainingTags
        };
      }
      
      return {
        ...state,
        tags: {
          ...state.tags,
          [tagId]: { ...currentTag, mode: newMode }
        }
      };
    }
    
    case 'SET_SEARCH_TERM':
      return {
        ...state,
        searchTerm: action.term
      };
    
    case 'SET_GLOBAL_LOGIC':
      return {
        ...state,
        globalLogic: action.logic
      };
    
    case 'RESET_FILTERS':
      return {
        tags: {},
        searchTerm: '',
        globalLogic: 'AND'
      };
    
    case 'INITIALIZE_FROM_URL':
      return {
        ...state,
        ...action.state
      };
    
    default:
      return state;
  }
}

// Initial state
const initialState: FilterState = {
  tags: {},
  searchTerm: '',
  globalLogic: 'AND'
};

// Context interface
interface FilterContextType {
  state: FilterState;
  dispatch: React.Dispatch<FilterAction>;
}

// Create context
const FilterContext = createContext<FilterContextType | undefined>(undefined);

// Filter provider component
export const SimpleFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(filterReducer, initialState);
  
  // Initialize from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const initialState: Partial<FilterState> = {};
    
    // Parse search term
    const searchTerm = urlParams.get('q');
    if (searchTerm) {
      initialState.searchTerm = searchTerm;
    }
    
    // Parse global logic
    const globalLogic = urlParams.get('logic');
    if (globalLogic === 'AND' || globalLogic === 'OR') {
      initialState.globalLogic = globalLogic;
    }
    
    // Parse tags
    const tagParams = urlParams.getAll('tag');
    const tags: FilterState['tags'] = {};
    
    tagParams.forEach(tagParam => {
      const [mode, ...valueParts] = tagParam.split(':');
      const value = valueParts.join(':'); // Handle values with colons
      
      if ((mode === 'inclusive' || mode === 'required' || mode === 'excluded') && value) {
        const tagId = `${mode}-${value}`;
        tags[tagId] = { value, mode };
      }
    });
    
    if (Object.keys(tags).length > 0) {
      initialState.tags = tags;
    }
    
    // Initialize state if we have URL parameters
    if (Object.keys(initialState).length > 0) {
      dispatch({ type: 'INITIALIZE_FROM_URL', state: initialState });
    }
  }, []);
  
  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    
    // Add tags to URL
    Object.entries(state.tags).forEach(([tagId, { value, mode }]) => {
      params.append('tag', `${mode}:${value}`);
    });
    
    // Add search term
    if (state.searchTerm.trim()) {
      params.set('q', state.searchTerm.trim());
    }
    
    // Add global logic (only if not default AND)
    if (state.globalLogic === 'OR') {
      params.set('logic', state.globalLogic);
    }
    
    // Update URL without triggering navigation
    const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, [state]);
  
  return (
    <FilterContext.Provider value={{ state, dispatch }}>
      {children}
    </FilterContext.Provider>
  );
};

// Custom hook to use the filter context
export const useSimpleFilter = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useSimpleFilter must be used within a SimpleFilterProvider');
  }
  return context;
};

// Helper function to check if any filters are active
export const hasActiveFilters = (state: FilterState): boolean => {
  return Object.keys(state.tags).length > 0 || state.searchTerm.trim().length > 0;
};

// Helper function to get tag count by mode
export const getTagCountByMode = (state: FilterState) => {
  const counts = { inclusive: 0, required: 0, excluded: 0 };
  Object.values(state.tags).forEach(tag => {
    counts[tag.mode]++;
  });
  return counts;
}; 